import Vue from 'vue';
import VueRouter from 'vue-router';
import VueMeta from 'vue-meta';
// eslint-disable-next-line import/no-cycle
import store from '@/store';
import SignIn from '../views/auth/SignIn';
import ResetPassword from '../views/auth/ResetPassword';

// Import Admin Views
import AdminDashboard from '@/admin/views/AdminDashboard.vue';
import UserManagement from '@/admin/components/UserManagement.vue';
import RoleManagement from '@/admin/components/RoleManagement.vue';
import ModuleManagement from '@/admin/components/ModuleManagement.vue';
import ProductSetup from '@/admin/components/ProductSetup.vue';

Vue.use(VueMeta);
Vue.use(VueRouter);

// Check authentication status on app load
store.dispatch('auth/checkAuthStatus');

// Route constants
const LOGIN_ROUTE = '/';
const DASHBOARD_ROUTE = '/dashboard';

// Getter constants
const IS_AUTHENTICATED = 'auth/isAuthenticated';
const IS_ADMIN = 'auth/isAdmin';
const IS_AGENT = 'auth/isAgent';

const ifNotAuthenticated = (_to, _from, next) => {
  if (!store.getters[IS_AUTHENTICATED]) {
    next();
    return;
  }
  next(DASHBOARD_ROUTE);
};

const requiresAuth = (to, _from, next) => {
  if (!store.getters[IS_AUTHENTICATED]) {
    next(LOGIN_ROUTE);
    return;
  }
  next();
};

const requiresAdmin = (to, _from, next) => {
  if (!store.getters[IS_AUTHENTICATED]) {
    next(LOGIN_ROUTE);
    return;
  }

  if (!store.getters[IS_ADMIN]) {
    next(DASHBOARD_ROUTE);
    return;
  }

  next();
};

const requiresAgent = (to, _from, next) => {
  if (!store.getters[IS_AUTHENTICATED]) {
    next(LOGIN_ROUTE);
    return;
  }

  if (!store.getters[IS_AGENT] && !store.getters[IS_ADMIN]) {
    next(DASHBOARD_ROUTE);
    return;
  }

  next();
};

const routes = [
  {
    path: '/',
    name: 'SignIn',
    beforeEnter: ifNotAuthenticated,
    component: SignIn,
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    beforeEnter: ifNotAuthenticated,
    component: ResetPassword,
  },
  {
    path: '/password-setup',
    name: 'PasswordSetup',
    beforeEnter: ifNotAuthenticated,
    component: () => import('../views/auth/PasswordSetup'),
    meta: { requiresAuth: false, hideNavigation: true },
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    beforeEnter: requiresAuth,
    component: () => import('../views/Dashboard'),
    meta: { keepAlive: true, requiresAuth: true },
  },
  {
    path: '/products',
    name: 'Products',
    component: () => import('../views/Products'),
  },
  {
    path: '/products/rfq/elimu-plan',
    name: 'ElimuPlan',
    component: () => import('../views/rfq/ElimuPlan'),
    meta: { keepAlive: true },
  },
  {
    path: '/products/rfq/Imarika',
    name: 'Imarika',
    component: () => import('../views/rfq/ImarikaPlan'),
  },
  {
    path: '/products/rfq/pumzisha-plan',
    name: 'PumzishaPlan',
    component: () => import('../views/rfq/PumzishaPlan.vue'),
  },
  {
    path: '/products/rfq/ElimuPlus-plan',
    name: 'ElimuPlusPlan',
    component: () => import('../views/rfq/ElimuPlusPlan.vue'),
  },
  {
    path: '/products/rfq/akiba-halisi-plan',
    name: 'AkibaPlan',
    component: () => import('../views/rfq/AkibaHalisiPlan'),
  },
  {
    path: '/products/rfq/term-assurance-plan',
    name: 'TermAssurancePlan',
    component: () => import('../views/rfq/TermAssurancePlan'),
  },
  {
    path: '/products/rfq/whole-life-plan',
    name: 'WholeLifePlan',
    component: () => import('../views/rfq/WholeLifePlan'),
  },
  {
    path: '/products/rfq/wealth-builder-plan',
    name: 'WealthBuilderPlan',
    component: () => import('../views/rfq/WealthBuilderPlan'),
  },
  {
    path: '/products/rfq/keyman-plan',
    name: 'KeymanPlan',
    component: () => import('../views/rfq/KeymanPlan'),
  },
  {
    path: '/products/rfq/individual-pension-plan',
    name: 'IndividualPensionPlan',
    component: () => import('../views/rfq/IndividualPensionPlan.vue'),
  },
  {
    path: '/cover/agent-guide',
    name: 'AgentGuide',
    beforeEnter: requiresAgent,
    component: () => import('../views/cover/Guide'),
    meta: { requiresAuth: true, requiresAgent: true },
  },
  {
    path: '/cover/buy-cover',
    name: 'BuyCover',
    beforeEnter: requiresAgent,
    component: () => import('../views/cover/BuyCover'),
    meta: { requiresAuth: true, requiresAgent: true },
  },
  {
    path: '/cover/proposals',
    name: 'Proposals',
    beforeEnter: requiresAgent,
    component: () => import('../views/cover/Proposals'),
    meta: { requiresAuth: true, requiresAgent: true },
  },
  {
    path: '/cover/policies',
    name: 'Policies',
    beforeEnter: requiresAgent,
    component: () => import('../views/cover/Policies'),
    meta: { requiresAuth: true, requiresAgent: true },
  },
  {
    path: '/error',
    name: 'ServerError',
    component: () => import('../views/errors/Error50x'),
    meta: { noNavigation: true },
  },
  {
    path: '/no-internet',
    name: 'NoInternet',
    component: () => import('../views/errors/Connection'),
    meta: { noNavigation: true },
  },
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: AdminDashboard,
    beforeEnter: requiresAdmin,
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      {
        path: 'AdminDashboard',
        name: 'AdminHome',
        component: AdminDashboard,
        meta: { requiresAuth: true, requiresAdmin: true },
      },
      {
        path: 'UserManagement',
        name: 'UserManagement',
        component: UserManagement,
        meta: { requiresAuth: true, requiresAdmin: true },
      },
      {
        path: 'RoleManagement',
        name: 'RoleManagement',
        component: RoleManagement,
        meta: { requiresAuth: true, requiresAdmin: true },
      },
      {
        path: 'ModuleManagement',
        name: 'ModuleManagement',
        component: ModuleManagement,
        meta: { requiresAuth: true, requiresAdmin: true },
      },
      {
        path: 'ProductSetup',
        name: 'ProductSetup',
        component: ProductSetup,
        meta: { requiresAuth: true, requiresAdmin: true },
      },
    ],
  },
  {
    path: '*',
    name: 'NotFound',
    component: () => import('../views/errors/Error404'),
    meta: { noNavigation: true },
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  // eslint-disable-next-line no-unused-vars
  scrollBehavior(to, _from, _savedPosition) {
    if (to.hash) {
      return { selector: to.hash };
    }
    return { x: 0, y: 0 };
  },
  routes,
});

// Global navigation guard
router.beforeEach((to, from, next) => {
  // Check if route requires authentication
  if (to.meta.requiresAuth && !store.getters[IS_AUTHENTICATED]) {
    next(LOGIN_ROUTE);
    return;
  }

  // Check if route requires admin role
  if (to.meta.requiresAdmin && !store.getters[IS_ADMIN]) {
    if (!store.getters[IS_AUTHENTICATED]) {
      next(LOGIN_ROUTE);
    } else {
      next(DASHBOARD_ROUTE);
    }
    return;
  }

  // Check if route requires agent role
  if (to.meta.requiresAgent && !store.getters[IS_AGENT] && !store.getters[IS_ADMIN]) {
    if (!store.getters[IS_AUTHENTICATED]) {
      next(LOGIN_ROUTE);
    } else {
      next(DASHBOARD_ROUTE);
    }
    return;
  }

  next();
});

export default router;
